import Input from 'components/inputs/Input';
import ConfirmModal from 'components/Modal/ConfirmModal';
import React, { useState } from 'react';
import styled from 'styled-components';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 460px;
`;

const Explanation = styled.p`
  margin: 0;
  font-size: 0.9em;
  color: rgb(90, 90, 90);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

interface Props {
  registrationNumber?: string;
  route: string;
  /** Nuvarande notering, när låset släpps eller skrivs om. */
  currentNote?: string;
  reserved: boolean;
  onCancel(): void;
  onConfirm(note: string | undefined): void;
}

/**
 * Låser ett uppdrag för manuell planering, eller släpper det.
 *
 * Noteringen är inte en formalitet: ett uppdrag som försvinner ur poolen utan förklaring ser för
 * nästa planerare ut som en bugg. Mötets exempel — "hemresa för Kalle, hemleverans Överkalix" —
 * är precis den mening som gör låset begripligt för någon annan än den som satte det.
 */
const ReserveAssignmentModal: React.FC<Props> = ({
  registrationNumber,
  route,
  currentNote,
  reserved,
  onCancel,
  onConfirm,
}) => {
  const [note, setNote] = useState(currentNote ?? '');

  if (!reserved) {
    return (
      <ConfirmModal
        confirmText="Släpp uppdraget"
        content={
          <Body>
            <Explanation>
              <b>{registrationNumber || 'Uppdraget'}</b> {route} går tillbaka i poolen och kan
              tas av nästa körning.
            </Explanation>
            {currentNote && <Explanation>Noteringen som tas bort: “{currentNote}”</Explanation>}
          </Body>
        }
        onCancel={onCancel}
        onConfirm={() => onConfirm(undefined)}
        title="Släpp reservationen"
      />
    );
  }

  return (
    <ConfirmModal
      confirmText="Lås för manuell planering"
      content={
        <Body>
          <Explanation>
            <b>{registrationNumber || 'Uppdraget'}</b> {route} utesluts ur poolen tills låset
            släpps. Det visas kvar i den manuella listan med din notering, så att ingen undrar
            vart det tog vägen.
          </Explanation>
          <Field>
            Notering
            <Input
              autoFocus
              onChange={(e) => setNote(e.currentTarget.value)}
              placeholder="t.ex. hemresa för Kalle, hemleverans Överkalix"
              value={note}
              width="100%"
            />
          </Field>
        </Body>
      }
      onCancel={onCancel}
      onConfirm={() => onConfirm(note.trim() || undefined)}
      title="Reservera för manuell planering"
    />
  );
};

export default ReserveAssignmentModal;
